/**
 * A playground session as a structured Markdown file.
 *
 * The point is the handoff: ops tests in the playground, saves the transcript,
 * and attaches the file to feedback — a self-contained document that carries
 * the session's identity (brand, acting-as email, date) with every exchange,
 * so the conversation being discussed never has to be reconstructed from
 * screenshots.
 */

export interface TranscriptMeta {
  sessionId: string;
  /** The sequential playground ticket number (from 1000) ops quotes in feedback. */
  ticketNumber?: number | null;
  brandName: string;
  customerEmail: string | null;
  openedAt?: string | null;
  author?: string | null;
}

export interface TranscriptTurn {
  message: string;
  /** Zoye's reply, when one exists. */
  reply: string | null;
  /** What happened instead of a reply — escalation reason, error, pending. */
  note?: string | null;
  /**
   * A draft a rule BLOCKED — written, never delivered. Rendered as such:
   * ticket #1051's blocked pregnancy deflection exported as "no reply was
   * stored", and ops read two days of silence where the system had actually
   * written the right answer and escalated.
   */
  blockedDraft?: string | null;
}

/** The per-run outcome the session detail endpoint returns, oldest first. */
export interface SessionRunOutcome {
  escalated: boolean;
  escalationReason: string | null;
  blockedDraft: string | null;
}

/**
 * Pair a stored session's messages into transcript turns, with each turn's
 * run outcome explaining any silence.
 *
 * A customer message opens a turn and takes the next run outcome (one run
 * per turn, oldest first); the agent message that follows rides along as the
 * delivered reply and clears the note. A turn left without a reply keeps the
 * outcome's explanation: a blocked draft, an escalation, or — when no run
 * exists at all — the honest "nothing stored".
 */
export function pairSessionTurns(
  messages: ReadonlyArray<{ role: string; body: string }>,
  runOutcomes?: ReadonlyArray<SessionRunOutcome> | null,
): TranscriptTurn[] {
  const turns: TranscriptTurn[] = [];
  let turnIndex = 0;
  for (const entry of messages) {
    if (entry.role === 'customer') {
      const outcome = runOutcomes?.[turnIndex] ?? null;
      turnIndex += 1;
      turns.push({
        message: entry.body,
        reply: null,
        blockedDraft: outcome?.blockedDraft ?? null,
        note: outcome?.blockedDraft
          ? `a rule blocked the draft (${outcome.escalationReason ?? 'no reason recorded'}) and the ticket escalated to a human`
          : outcome?.escalated
            ? `escalated to a human (${outcome.escalationReason ?? 'no reason recorded'}) — the agent stays silent once a person owns the ticket`
            : 'no reply was stored',
      });
    } else if (turns.length > 0) {
      const last = turns[turns.length - 1]!;
      last.reply = entry.body;
      last.note = null;
    }
  }
  return turns;
}

export function buildTranscriptMarkdown(
  meta: TranscriptMeta,
  turns: readonly TranscriptTurn[],
): string {
  const ticket = meta.ticketNumber != null ? `ticket #${meta.ticketNumber}` : null;
  const lines: string[] = [
    `# Playground ${ticket ?? 'session'} — ${meta.brandName}`,
    '',
    // The ticket number leads: it is the reference feedback is filed under.
    ...(ticket ? [`- **Ticket**: #${meta.ticketNumber}`] : []),
    `- **Session**: \`${meta.sessionId}\``,
    `- **Brand**: ${meta.brandName}`,
    `- **Acting as**: ${meta.customerEmail ?? '(no customer email — empty account)'}`,
    ...(meta.openedAt ? [`- **Started**: ${new Date(meta.openedAt).toLocaleString()}`] : []),
    ...(meta.author ? [`- **Tested by**: ${meta.author}`] : []),
    `- **Exported**: ${new Date().toLocaleString()}`,
    '',
    '> Writes are always simulated in the playground — nothing here reached a customer or moved money.',
    '',
  ];

  turns.forEach((turn, index) => {
    lines.push(`## Turn ${index + 1}`, '', '**You, as the customer:**', '', quote(turn.message), '');
    if (turn.reply) {
      lines.push('**Zoye drafts:**', '', quote(turn.reply), '');
    } else if (turn.blockedDraft) {
      lines.push(
        `**Zoye drafted, but a rule BLOCKED it — never sent** (${turn.note ?? 'no reason recorded'}):`,
        '',
        quote(turn.blockedDraft),
        '',
      );
    } else {
      lines.push(`**No reply** — ${turn.note ?? 'nothing was stored for this message'}.`, '');
    }
  });

  return lines.join('\n');
}

function quote(text: string): string {
  return text
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n');
}

/** Browser download, no server round trip — the transcript is already here. */
export function downloadMarkdown(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * pg-session file name, filesystem-safe. The ticket number leads when it
 * exists — "feedback on ticket 1042" should match the attachment at a glance —
 * with the short session id as the fallback for pre-numbering exports.
 */
export function transcriptFilename(meta: TranscriptMeta): string {
  const brand = meta.brandName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const day = (meta.openedAt ? new Date(meta.openedAt) : new Date()).toISOString().slice(0, 10);
  const ref = meta.ticketNumber != null ? `ticket-${meta.ticketNumber}` : meta.sessionId.slice(0, 8);
  return `playground-${ref}-${brand}-${day}.md`;
}
